import { useTranslation } from "react-i18next";
import { DEFAULT_IMAGE, SITE_NAME, absoluteUrl } from "./site";

type SeoProps = {
  title: string;
  description: string;
  path: string; // caminho da página sem domínio, ex.: "/blog/3"
  image?: string; // import do CRA ("/static/media/...") ou URL absoluta
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: object | object[];
};

const OG_LOCALE: Record<string, string> = { pt: "pt_BR", en: "en_US", es: "es_ES" };

// Metadados da página. O React 19 move <title>, <meta> e <link> para o <head> sozinho e os remove ao trocar de página.
// data-seo marca as tags para o index.tsx apagar as que vieram prontas do pré-render antes de o React criar as dele.
export default function Seo({ title, description, path, image, type = "website", noindex, jsonLd }: SeoProps) {
  const { i18n } = useTranslation();
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const url = absoluteUrl(path);
  const imageUrl = absoluteUrl(image || DEFAULT_IMAGE);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <title data-seo="">{fullTitle}</title>
      <meta data-seo="" name="description" content={description} />
      <link data-seo="" rel="canonical" href={url} />
      {noindex && <meta data-seo="" name="robots" content="noindex, follow" />}
      <meta data-seo="" property="og:title" content={fullTitle} />
      <meta data-seo="" property="og:description" content={description} />
      <meta data-seo="" property="og:url" content={url} />
      <meta data-seo="" property="og:image" content={imageUrl} />
      <meta data-seo="" property="og:type" content={type} />
      <meta data-seo="" property="og:locale" content={OG_LOCALE[i18n.language?.slice(0, 2)] ?? "pt_BR"} />
      <meta data-seo="" name="twitter:card" content="summary_large_image" />
      {schemas.map((schema, i) => (
        <script
          key={i}
          data-seo=""
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ "@context": "https://schema.org", ...schema }).replace(/</g, "\\u003c"),
          }}
        />
      ))}
    </>
  );
}

// Páginas fixas: title e description vêm de seo.<page> nos locales.
export function PageSeo({ page, path, noindex, jsonLd }: { page: string; path: string; noindex?: boolean; jsonLd?: object | object[] }) {
  const { t } = useTranslation();
  return (
    <Seo
      title={t(`seo.${page}.title`)}
      description={t(`seo.${page}.description`)}
      path={path}
      noindex={noindex}
      jsonLd={jsonLd}
    />
  );
}
